import { jsx as _jsx } from "react/jsx-runtime";
import { compact } from 'lodash-es';
import { InvoiceDisplayStatusV2, PaymentDetailType } from 'anchor-scout-common/types';
import { AccountingStatusFilter } from '~/business-logic/qboStatusFilterLogic';
import { SearchFilterType } from '~/components/TableFilters/types';
import { DateFilterPicker, DateFilterPickerPresets } from './DateFilterPicker';
export const invoiceFilterTextsPath = 'invoice';
export var FilterType = /*#__PURE__*/ function(FilterType) {
    FilterType["InvoiceStatus"] = "invoiceStatus";
    FilterType["Date"] = "date";
    FilterType["QboSync"] = "qboSync";
    FilterType["XeroSync"] = "xeroSync";
    FilterType["IssuesPreventingPayment"] = "issuesPreventingPayment";
    FilterType["ProcessedBy"] = "processedBy";
    return FilterType;
}({});
export function getTableFilterLabels(filterLabels) {
    return {
        ["invoiceStatus"]: filterLabels.invoiceStatus(),
        ["date"]: filterLabels.date(),
        ["qboSync"]: filterLabels.qboSync(),
        ["xeroSync"]: filterLabels.xeroSync(),
        ["issuesPreventingPayment"]: filterLabels.issuesPreventingPayment(),
        ["processedBy"]: filterLabels.processedBy()
    };
}
export function getActiveFilterGroups(params) {
    const allFilters = compact([
        "invoiceStatus",
        !params.hideDateChip && "date",
        params.showQboIntegration && params.hasQboFilterOptions && "qboSync",
        params.showXeroIntegration && params.hasXeroFilterOptions && "xeroSync",
        "issuesPreventingPayment",
        params.isVendor && "processedBy"
    ]);
    return [
        {
            label: '',
            filters: allFilters
        }
    ];
}
export function invoiceDisplayStatusLabels(labels) {
    return {
        [InvoiceDisplayStatusV2.Issued]: labels.issued(),
        [InvoiceDisplayStatusV2.Pending]: labels.pending(),
        [InvoiceDisplayStatusV2.Overdue]: labels.overdue(),
        [InvoiceDisplayStatusV2.Processing]: labels.processing(),
        [InvoiceDisplayStatusV2.Paid]: labels.paid(),
        [InvoiceDisplayStatusV2.Voided]: labels.voided(),
        [InvoiceDisplayStatusV2.Refunded]: labels.refunded(),
        [InvoiceDisplayStatusV2.RefundFailed]: labels.refundFailed(),
        [InvoiceDisplayStatusV2.Disputed]: labels.disputed(),
        [InvoiceDisplayStatusV2.PartiallyRefunded]: labels.partiallyRefunded()
    };
}
function datePresetLabels(labels) {
    return {
        [DateFilterPickerPresets.AllTime]: labels.allTime(),
        [DateFilterPickerPresets.Today]: labels.today(),
        [DateFilterPickerPresets.ThisWeek]: labels.thisWeek(),
        [DateFilterPickerPresets.ThisMonth]: labels.thisMonth(),
        [DateFilterPickerPresets.PreviousMonth]: labels.previousMonth(),
        [DateFilterPickerPresets.LastThreeMonths]: labels.lastThreeMonths()
    };
}
function InvoiceDateFilter(param) {
    let { value, onChange } = param;
    return /*#__PURE__*/ _jsx(DateFilterPicker, {
        value: value,
        onChange: onChange
    });
}
function accountingStatusLabels(labels) {
    return {
        [AccountingStatusFilter.All]: labels.all(),
        [AccountingStatusFilter.Synced]: labels.synced(),
        [AccountingStatusFilter.NotSynced]: labels.notSynced()
    };
}
function paymentDetailLabels(labels) {
    return {
        [PaymentDetailType.MissingPaymentMethod]: labels.missingPaymentMethod(),
        [PaymentDetailType.PaymentMethodNotVerified]: labels.paymentMethodNotVerified(),
        [PaymentDetailType.ClientApprovalRequired]: labels.clientApprovalRequired(),
        [PaymentDetailType.PaymentStopped]: labels.paymentStopped(),
        [PaymentDetailType.PaymentValidationRequired]: labels.paymentValidationRequired(),
        [PaymentDetailType.PaymentAwaitingInitiation]: labels.paymentAwaitingInitiation(),
        [PaymentDetailType.PaymentScheduled]: labels.paymentScheduled(),
        [PaymentDetailType.PaymentInitiated]: labels.paymentInitiated(),
        [PaymentDetailType.PaymentCollected]: labels.paymentCollected(),
        [PaymentDetailType.RefundRequested]: labels.refundRequested(),
        [PaymentDetailType.Refunded]: labels.refunded(),
        [PaymentDetailType.PaidExternally]: labels.paidExternally(),
        [PaymentDetailType.PaymentFailed]: labels.paymentFailed(),
        [PaymentDetailType.PaidOnDate]: labels.paidOnDate(),
        [PaymentDetailType.Voided]: labels.voided(),
        [PaymentDetailType.Disputed]: labels.disputed(),
        [PaymentDetailType.RefundFailed]: labels.refundFailed()
    };
}
export function getTableFilterConfig(params) {
    return {
        ["invoiceStatus"]: {
            type: SearchFilterType.MultiSelectFilter,
            removable: false,
            options: params.invoiceStatusFilterOptions.map((o)=>({
                    value: o.value,
                    label: o.label,
                    counter: o.counter
                }))
        },
        ["date"]: {
            type: SearchFilterType.CustomFilter,
            removable: false,
            component: InvoiceDateFilter,
            initialValue: DateFilterPickerPresets.AllTime
        },
        ["qboSync"]: {
            type: SearchFilterType.RadioFilter,
            removable: true,
            options: params.qboStatusFilterOptions.map((o)=>({
                    value: o.value,
                    label: o.label,
                    sublabel: "(".concat(o.counter, ")")
                })),
            defaultOption: AccountingStatusFilter.All
        },
        ["xeroSync"]: {
            type: SearchFilterType.RadioFilter,
            removable: true,
            options: params.xeroStatusFilterOptions.map((o)=>({
                    value: o.value,
                    label: o.label,
                    sublabel: "(".concat(o.counter, ")")
                })),
            defaultOption: AccountingStatusFilter.All
        },
        ["issuesPreventingPayment"]: {
            type: SearchFilterType.MultiSelectFilter,
            removable: true,
            hideSelectAll: true,
            options: params.issuesPreventingPaymentFilterOptions.map((o)=>({
                    value: o.value,
                    label: o.label,
                    counter: o.counter
                }))
        },
        ["processedBy"]: {
            type: SearchFilterType.MultiSelectFilter,
            removable: true,
            options: params.processedByFilterOptions.map((o)=>({
                    value: o.value,
                    label: o.label,
                    counter: o.counter
                }))
        }
    };
}
export function formatFilterDescription(params) {
    const { filterType, value, texts } = params;
    switch(filterType){
        case "invoiceStatus":
            {
                const statuses = value;
                if (!statuses.length) return undefined;
                const labels = invoiceDisplayStatusLabels(texts.invoiceDisplayStatusLabels);
                return statuses.map((s)=>labels[s]).join(', ');
            }
        case "date":
            {
                const labels = datePresetLabels(texts.datePresetLabels);
                return labels[value];
            }
        case "qboSync":
        case "xeroSync":
            {
                const labels = accountingStatusLabels(texts.accountingStatusLabels);
                return labels[value];
            }
        case "issuesPreventingPayment":
            {
                const details = value;
                if (!details.length) return undefined;
                const labels = paymentDetailLabels(texts.paymentDetailLabels);
                return details.map((d)=>labels[d]).join(', ');
            }
        case "processedBy":
            {
                const details = value;
                if (!details.length) return undefined;
                const labels = {
                    anchor: texts.processedByLabels.anchor(),
                    external: texts.processedByLabels.external()
                };
                return details.map((d)=>labels[d]).join(', ');
            }
    }
}
